// WLTP data types

import { IMarket } from 'data/schema';
import { EConsumptionTextKeys, TConsumptionTextData } from 'utils/wltp';
import { z } from 'zod';

export enum EWltpEngineTypes {
  Electric = 'E',
  Hybrid = 'H',
  MildHybrid = 'MH',
  Petrol = 'P',
}

export interface IConsumptionValue {
  consumption?: number;
  consumptionUnit?: string;
  emission?: number;
  emissionUnit?: string;
  range?: number;
  rangeUnit?: string;
  secondaryConsumption?: number;
  secondaryConsumptionUnit?: string;
  dischargedBatteryConsumption?: number;
  dischargedBatteryConsumptionUnit?: string;
  dischargedBatteryEmission?: number;
  dischargedBatteryEmissionUnit?: string;
  dischargedBatteryEfficiencyClass?: string;
  efficiencyClass?: string;
  isHybrid?: boolean;
  type?: EWltpEngineTypes;
  modelYear?: number; // specific to vsrs
  isElectric?: boolean; // specific to vsrs
  isRequiredForModel?: boolean; // some VW commercial vehicles do not require WLTP data
}

export type TEngineType = 'ICE' | 'NOVC_HEV' | 'PEV' | 'OVC_HEV';

export type TGeneralDataValue = { key: string; value: number; unit: string };

export type TEnergyManagementType = 'PURE' | 'SUSTAINING' | 'WEIGHTED';

// WLTP text types

export type TConsumptionTextPerEngine = {
  [key in EWltpEngineTypes]: EConsumptionTextKeys[];
};

export type TConsumptionRules = Partial<{
  [market in IMarket]: TConsumptionTextPerEngine;
}> & {
  default: TConsumptionTextPerEngine;
};

// Additional details types

export interface ICarDetails {
  title?: string; // Vehicle title/model name
  description?: string; // Vehicle description (from TYPE option)
  fuelType?: string; // Kraftstoffart (Fuel Type)
  displacement?: string; // Hubraum (Displacement)
  power?: string; // Leistung (Power)
  transmission?: string; // Getriebe (Transmission)
  driveType?: string; // Antrieb (Drive Type)
  exteriorColor?: string; // Außenfarbe (Exterior Color)
  seatCoverColor?: string; // Sitzbezug (Seat Cover Color)
  upholsteryType?: string; // Polsterart (Upholstery Type/Material)
  modelYear?: number; // Model year, only set together with outdated3D
  outdated3D?: boolean; // Marks a 3+ year old car served for 2D-only display
}

/**
 * Zod schema for ICarDetails
 * Validates the car details structure
 */
export const carDetailsSchema = z
  .object({
    title: z.string().optional(),
    description: z.string().optional(),
    fuelType: z.string().optional(),
    displacement: z.string().optional(),
    power: z.string().optional(),
    transmission: z.string().optional(),
    driveType: z.string().optional(),
    exteriorColor: z.string().optional(),
    seatCoverColor: z.string().optional(),
    upholsteryType: z.string().optional(),
    modelYear: z.number().optional(),
    outdated3D: z.boolean().optional(),
  })
  .strict(); // Don't allow additional properties

export type TCarDetails = z.infer<typeof carDetailsSchema>;

// VTP-specific types (stock car data)

/**
 * VTP details for stock cars (only available when resolved via VTP API)
 * Contains extended data not available from VSRS: price, mileage, registration
 */
export interface IVtpDetails {
  firstRegistration?: string; // ISO date string
  mileage?: string; // Formatted with unit (e.g., "98.000 km")
  price?: string; // Formatted sale price (e.g., "11.890,00 €")
  commissionNumber?: string; // knr field - Kommissionsnummer
}

/**
 * VTP photo metadata
 */
export interface IVtpPhoto {
  url: string;
  isRealPhoto: boolean; // true for dealer photos, false for renders
  width?: number;
  height?: number;
}

/**
 * Zod schema for VTP details
 */
export const vtpDetailsSchema = z
  .object({
    firstRegistration: z.string().optional(),
    mileage: z.string().optional(),
    price: z.string().optional(),
    commissionNumber: z.string().optional(),
  })
  .strict();

/**
 * Zod schema for VTP photo
 */
export const vtpPhotoSchema = z.object({
  url: z.string(),
  isRealPhoto: z.boolean(),
  width: z.number().optional(),
  height: z.number().optional(),
});

export type TVtpDetails = z.infer<typeof vtpDetailsSchema>;
export type TVtpPhoto = z.infer<typeof vtpPhotoSchema>;

// API types

export interface IVehicleData {
  brand?: string;
  model?: string;
  prString: string;
  wltpData: IConsumptionValue;
  wltpTextData?: TConsumptionTextData;
  locale?: string;
  modelRevision?: string;
  carDetails?: ICarDetails;
  // VTP-specific fields (only populated when resolved via VTP API)
  isVtp?: boolean;
  vtpDetails?: IVtpDetails;
  vtpPhotos?: IVtpPhoto[];
}

// A provider result before the handler serves it: a null modelRevision means GMC failed transiently
export type TProviderVehicleData = Omit<IVehicleData, 'modelRevision'> & { modelRevision?: string | null };

export type TJwt = {
  access_token: string;
  token_type: string;
  expires_in: string;
  scope: string;
  // this is not a natural Jwt Property, but we need it to figure out if a jwt is expired
  expiration_date: number;
};

export type TGmcConfigurationsInput = {
  modelCode: string;
  modelYear: string;
  exterior: {
    code: string;
  };
  interior: {
    code: string;
  };
};
